var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { margin } from 'styled-system';
import { muiBankTheme } from '../tools/theme';
const StyledSVG = styled.svg `
  ${({ pathTransform, rotate, $animateRotation }) => `
    display: inline-block;
    stroke-width: 0;
    transform: rotate(${rotate}deg);
    vertical-align: text-bottom;
    
    ${$animateRotation
    ? css `
            transition: transform 0.25s ease;
          `
    : css ``}

    path, polygon {
      transform: ${pathTransform};
    }
  `}
`;
const PathsWrapperFallback = ({ children, }) => React.createElement(React.Fragment, null, children);
const Icon = React.forwardRef((_a, ref) => {
    var { icon, size, unsupportedCustomWidth, unsupportedCustomHeight, fill: customFill, rotate: customRotate, customTitle, ariaLabelledBy, ariaHidden, animateRotation = false, legacyBehavior = false } = _a, others = __rest(_a, ["icon", "size", "unsupportedCustomWidth", "unsupportedCustomHeight", "fill", "rotate", "customTitle", "ariaLabelledBy", "ariaHidden", "animateRotation", "legacyBehavior"]);
    const safeFill = customFill ? muiBankTheme.colors[customFill] : undefined;
    const width = useMemo(() => {
        const bankThemeBaseIconSize = legacyBehavior
            ? muiBankTheme.baseIconSizeDEPRECATED[size || 'md']
            : muiBankTheme.baseIconSize[size || 'md'];
        return unsupportedCustomWidth || bankThemeBaseIconSize;
    }, [size, unsupportedCustomWidth]);
    const height = useMemo(() => {
        const bankThemeBaseIconSize = legacyBehavior
            ? muiBankTheme.baseIconSizeDEPRECATED[size || 'md']
            : muiBankTheme.baseIconSize[size || 'md'];
        return unsupportedCustomHeight || bankThemeBaseIconSize;
    }, [size, unsupportedCustomHeight]);
    const isSmDown = useMemo(() => {
        // this is 20, but wanted to show how we got to that value
        const midPoint = (24 + 16) / 2;
        return width * height < midPoint * midPoint;
    }, [width, height]);
    const iconMetadata = useMemo(() => {
        if (icon.smIcon && isSmDown) {
            return icon.smIcon;
        }
        if (icon.lgIcon && !isSmDown) {
            return icon.lgIcon;
        }
        return icon.base;
    }, [icon, isSmDown]);
    const paths = useMemo(() => {
        return iconMetadata.paths.map((_a) => {
            var { path, fill } = _a, otherProps = __rest(_a, ["path", "fill"]);
            return (React.createElement("path", Object.assign({ key: path, d: path, fill: safeFill || fill }, otherProps)));
        });
    }, [iconMetadata, safeFill]);
    const pathTransform = useMemo(() => {
        const baseWidth = iconMetadata.width;
        const baseHeight = iconMetadata.height;
        const [x = 0, y = 0] = iconMetadata.translate || [];
        if (baseWidth >= baseHeight) {
            const scale = width / baseWidth;
            const translateY = (height - baseHeight * scale) / 2;
            return `scale(${scale}) translate(${x}px, ${translateY + y}px)`;
        }
        if (baseHeight > baseWidth) {
            const scale = height / baseHeight;
            const translateX = (width - baseWidth * scale) / 2;
            return `scale(${scale}) translate(${translateX + x}px, ${y}px)`;
        }
        return `scale(1.0) translate(${x}px, ${y})px`;
    }, [iconMetadata, height, width]);
    const PathsWrapper = iconMetadata.pathsWrapper || PathsWrapperFallback;
    const includeTitle = !ariaLabelledBy && !ariaHidden;
    return (React.createElement(StyledSVG, Object.assign({ role: "img", ref: ref, width: `${width}px`, height: `${height}px`, 
        /**
         * We want the viewBox to be a square, so we're taking the longest
         * side of the svg and setting both height and width to that.
         * The paths will center inside of the viewBox.
         */
        viewBox: `0 0 ${Math.max(width, height)} ${Math.max(width, height)}`, 
        /**
         * Paths must be scaled and translated in order to guarantee that
         * correct size and placement (height and width do not work in certain
         * circumstances).
         */
        pathTransform: pathTransform, rotate: (iconMetadata.rotate || 0) + (customRotate || 0), "$animateRotation": animateRotation }, others, (ariaLabelledBy && !includeTitle
        ? { 'aria-labelledby': ariaLabelledBy }
        : {}), (ariaHidden ? { 'aria-hidden': 'true' } : {})),
        includeTitle && React.createElement("title", null, customTitle || iconMetadata.title),
        React.createElement(PathsWrapper, { fill: safeFill }, paths)));
});
const IconWithMarginProps = styled(Icon)(margin);
const IconForProps = () => React.createElement(React.Fragment, null);
export { IconWithMarginProps as Icon, IconForProps };
//# sourceMappingURL=Icon.js.map